import React, { useEffect } from 'react';

const IdempotentOperations = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Idempotent Operations in Distributed Systems - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master idempotent operations for safe retries in distributed systems. Learn idempotency keys, patterns, and implementation strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master idempotent operations for safe retries in distributed systems. Learn idempotency keys, patterns, and implementation strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Idempotent Operations in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Enable safe retries and prevent duplicate side effects in distributed systems
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What are Idempotent Operations?</h2>
          <p style={styles.paragraph}>
            An idempotent operation is one that can be applied multiple times without changing the result beyond 
            the initial application. In distributed systems, idempotency is crucial for handling network failures, 
            timeouts, and retries safely. When an operation is idempotent, clients can safely retry requests 
            without worrying about unintended side effects.
          </p>
          <p style={styles.paragraph}>
            Idempotency is essential for building resilient systems where network partitions, service failures, 
            and client timeouts are common. It allows systems to implement retry logic, handle duplicate requests, 
            and maintain consistency even when operations are executed multiple times.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Types of Idempotency</h2>
          
          <h3 style={styles.heading}>Natural Idempotency</h3>
          <p style={styles.paragraph}>
            Some operations are naturally idempotent due to their inherent nature. These operations produce 
            the same result regardless of how many times they're executed.
          </p>
          <pre style={styles.codeBlock}>
{`Naturally Idempotent Operations:

1. HTTP GET Requests:
   GET /api/users/123
   - Reading data doesn't change state
   - Safe to retry multiple times
   - Same result every time (assuming no concurrent updates)

2. HTTP PUT Requests (when used correctly):
   PUT /api/users/123
   {
     "name": "John Doe",
     "email": "john@example.com"
   }
   - Sets resource to specific state
   - Multiple applications result in same final state

3. HTTP DELETE Requests:
   DELETE /api/users/123
   - First call deletes the resource
   - Subsequent calls have no effect (resource already deleted)
   - Result is the same: resource doesn't exist

4. Mathematical Operations:
   SET user_status = 'active'        # Always results in 'active'
   UPDATE balance = 1000 WHERE id=1  # Always sets balance to 1000
   
5. Absolute Assignments:
   user.status = 'inactive'  # Same final state regardless of repetition
   cache.set('key', 'value') # Same cache state after multiple calls

Non-Idempotent Operations:
❌ POST /api/users (creates new user each time)
❌ INCREMENT counter (changes value each time)  
❌ APPEND to log (adds entry each time)
❌ SEND email (sends multiple emails)
❌ CHARGE credit card (charges multiple times)`}
          </pre>

          <h3 style={styles.heading}>Engineered Idempotency</h3>
          <p style={styles.paragraph}>
            Many operations are not naturally idempotent but can be made idempotent through careful design 
            and implementation using techniques like idempotency keys, deduplication, and state checking.
          </p>
          <pre style={styles.codeBlock}>
{`Engineered Idempotency Techniques:

1. Idempotency Keys:
   POST /api/payments
   Headers:
     Idempotency-Key: "payment_123_abc"
   Body:
     {
       "amount": 100,
       "currency": "USD"
     }
   
   - Same key = same operation
   - Server tracks processed keys
   - Duplicate requests return same result

2. Check-Then-Act Pattern:
   if (!user.exists()) {
     user.create()
   }
   # Safe to retry - won't create duplicates

3. Upsert Operations:
   INSERT INTO users (id, name) VALUES (123, 'John')
   ON DUPLICATE KEY UPDATE name = 'John'
   # Creates if not exists, updates if exists

4. Version-Based Updates:
   UPDATE users SET name = 'John', version = version + 1
   WHERE id = 123 AND version = 5
   # Only succeeds once, subsequent calls fail gracefully

5. Conditional Operations:
   if (current_balance >= withdrawal_amount) {
     current_balance -= withdrawal_amount
   }
   # Safe to retry - won't overdraw account`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Idempotency Keys Implementation</h2>
          
          <h3 style={styles.heading}>Basic Idempotency Key System</h3>
          <p style={styles.paragraph}>
            Idempotency keys are unique identifiers that clients provide to ensure operations are processed 
            only once, even if the request is retried multiple times.
          </p>
          <pre style={styles.codeBlock}>
{`Idempotency Key Implementation:

import uuid
import time
import json
from typing import Optional, Dict, Any
from enum import Enum

class OperationStatus(Enum):
    PENDING = "pending"
    COMPLETED = "completed"
    FAILED = "failed"

class IdempotencyManager:
    def __init__(self, storage_backend):
        self.storage = storage_backend  # Redis, Database, etc.
        self.default_ttl = 86400  # 24 hours
    
    def execute_idempotent_operation(
        self, 
        idempotency_key: str, 
        operation_func: callable,
        *args, 
        **kwargs
    ) -> Dict[str, Any]:
        """
        Execute operation with idempotency guarantee
        
        Args:
            idempotency_key: Unique key for this operation
            operation_func: Function to execute
            *args, **kwargs: Arguments for the operation
            
        Returns:
            Operation result with metadata
        """
        
        # Check if operation already exists
        existing_operation = self.storage.get(f"idempotency:{idempotency_key}")
        
        if existing_operation:
            operation_data = json.loads(existing_operation)
            
            if operation_data['status'] == OperationStatus.COMPLETED.value:
                # Operation already completed, return cached result
                return {
                    'result': operation_data['result'],
                    'status': 'completed',
                    'cached': True,
                    'completed_at': operation_data['completed_at']
                }
            
            elif operation_data['status'] == OperationStatus.PENDING.value:
                # Operation in progress, check if it's stale
                if time.time() - operation_data['started_at'] > 300:  # 5 minutes
                    # Stale operation, allow retry
                    pass
                else:
                    # Operation still in progress
                    return {
                        'status': 'pending',
                        'message': 'Operation in progress',
                        'started_at': operation_data['started_at']
                    }
            
            elif operation_data['status'] == OperationStatus.FAILED.value:
                # Previous operation failed, allow retry
                pass
        
        # Record operation start
        operation_record = {
            'status': OperationStatus.PENDING.value,
            'started_at': time.time(),
            'args': args,
            'kwargs': kwargs
        }
        
        self.storage.setex(
            f"idempotency:{idempotency_key}",
            self.default_ttl,
            json.dumps(operation_record)
        )
        
        try:
            # Execute the operation
            result = operation_func(*args, **kwargs)
            
            # Record successful completion
            operation_record.update({
                'status': OperationStatus.COMPLETED.value,
                'result': result,
                'completed_at': time.time()
            })
            
            self.storage.setex(
                f"idempotency:{idempotency_key}",
                self.default_ttl,
                json.dumps(operation_record)
            )
            
            return {
                'result': result,
                'status': 'completed',
                'cached': False,
                'completed_at': operation_record['completed_at']
            }
            
        except Exception as e:
            # Record failure
            operation_record.update({
                'status': OperationStatus.FAILED.value,
                'error': str(e),
                'failed_at': time.time()
            })
            
            self.storage.setex(
                f"idempotency:{idempotency_key}",
                300,  # Shorter TTL for failed operations
                json.dumps(operation_record)
            )
            
            raise e

# Usage Example
import redis

redis_client = redis.Redis(host='localhost', port=6379, db=0)
idempotency_manager = IdempotencyManager(redis_client)

def create_payment(user_id: int, amount: float, currency: str):
    """Non-idempotent payment creation"""
    payment_id = str(uuid.uuid4())
    
    # Simulate payment processing
    print(f"Processing payment: $\{amount\} \{currency\} for user \{user_id\}")
    
    # This would typically involve:
    # 1. Validating user account
    # 2. Checking available balance
    # 3. Creating payment record
    # 4. Calling payment processor
    # 5. Recording transaction
    
    return {
        'payment_id': payment_id,
        'user_id': user_id,
        'amount': amount,
        'currency': currency,
        'status': 'completed',
        'created_at': time.time()
    }

# Client usage
def process_payment_request(user_id: int, amount: float, currency: str, client_request_id: str):
    """Process payment with idempotency"""
    
    try:
        result = idempotency_manager.execute_idempotent_operation(
            idempotency_key=f"payment_{user_id}_{client_request_id}",
            operation_func=create_payment,
            user_id=user_id,
            amount=amount,
            currency=currency
        )
        
        return result
        
    except Exception as e:
        return {
            'status': 'failed',
            'error': str(e)
        }

# Test idempotency
request_id = "req_123_abc"
user_id = 123
amount = 100.0
currency = "USD"

# First request
result1 = process_payment_request(user_id, amount, currency, request_id)
print("First request:", result1)

# Duplicate request (should return cached result)
result2 = process_payment_request(user_id, amount, currency, request_id)
print("Duplicate request:", result2)

# Both should have same payment_id and result2 should be cached`}
          </pre>

          <h3 style={styles.heading}>Database-Based Idempotency</h3>
          <p style={styles.paragraph}>
            For operations that involve database transactions, idempotency can be implemented using database 
            constraints and transaction isolation.
          </p>
          <pre style={styles.codeBlock}>
{`Database Idempotency Implementation:

-- Database schema for idempotency tracking
CREATE TABLE idempotency_keys (
    id SERIAL PRIMARY KEY,
    idempotency_key VARCHAR(255) UNIQUE NOT NULL,
    operation_type VARCHAR(100) NOT NULL,
    status VARCHAR(50) NOT NULL DEFAULT 'pending',
    request_data JSONB,
    response_data JSONB,
    created_at TIMESTAMP DEFAULT NOW(),
    updated_at TIMESTAMP DEFAULT NOW(),
    expires_at TIMESTAMP DEFAULT NOW() + INTERVAL '24 hours'
);

CREATE INDEX idx_idempotency_keys_key ON idempotency_keys(idempotency_key);
CREATE INDEX idx_idempotency_keys_expires ON idempotency_keys(expires_at);

-- Python implementation with database
import psycopg2
import json
from contextlib import contextmanager

class DatabaseIdempotencyManager:
    def __init__(self, db_connection):
        self.db = db_connection
    
    @contextmanager
    def transaction(self):
        """Database transaction context manager"""
        try:
            yield self.db
            self.db.commit()
        except Exception:
            self.db.rollback()
            raise
    
    def execute_idempotent_operation(
        self, 
        idempotency_key: str,
        operation_type: str,
        operation_func: callable,
        request_data: dict,
        *args, 
        **kwargs
    ):
        """Execute operation with database-backed idempotency"""
        
        with self.transaction():
            cursor = self.db.cursor()
            
            # Check for existing operation
            cursor.execute("""
                SELECT status, response_data, created_at
                FROM idempotency_keys 
                WHERE idempotency_key = %s
                AND expires_at > NOW()
            """, (idempotency_key,))
            
            existing = cursor.fetchone()
            
            if existing:
                status, response_data, created_at = existing
                
                if status == 'completed':
                    return {
                        'result': json.loads(response_data),
                        'status': 'completed',
                        'cached': True,
                        'created_at': created_at.isoformat()
                    }
                elif status == 'pending':
                    # Check if operation is stale
                    age = (datetime.now() - created_at).total_seconds()
                    if age < 300:  # 5 minutes
                        return {
                            'status': 'pending',
                            'message': 'Operation in progress'
                        }
            
            # Insert or update idempotency record
            cursor.execute("""
                INSERT INTO idempotency_keys 
                (idempotency_key, operation_type, status, request_data)
                VALUES (%s, %s, 'pending', %s)
                ON CONFLICT (idempotency_key) 
                DO UPDATE SET 
                    status = 'pending',
                    request_data = EXCLUDED.request_data,
                    updated_at = NOW()
            """, (idempotency_key, operation_type, json.dumps(request_data)))
            
            try:
                # Execute the operation
                result = operation_func(*args, **kwargs)
                
                # Update with success
                cursor.execute("""
                    UPDATE idempotency_keys 
                    SET status = 'completed', 
                        response_data = %s,
                        updated_at = NOW()
                    WHERE idempotency_key = %s
                """, (json.dumps(result), idempotency_key))
                
                return {
                    'result': result,
                    'status': 'completed',
                    'cached': False
                }
                
            except Exception as e:
                # Update with failure
                cursor.execute("""
                    UPDATE idempotency_keys 
                    SET status = 'failed',
                        response_data = %s,
                        updated_at = NOW(),
                        expires_at = NOW() + INTERVAL '1 hour'
                    WHERE idempotency_key = %s
                """, (json.dumps({'error': str(e)}), idempotency_key))
                
                raise e

# Usage with database transactions
def create_user_account(username: str, email: str, initial_balance: float):
    """Create user account with initial balance"""
    cursor = db.cursor()
    
    # Create user
    cursor.execute("""
        INSERT INTO users (username, email, created_at)
        VALUES (%s, %s, NOW())
        RETURNING id
    """, (username, email))
    
    user_id = cursor.fetchone()[0]
    
    # Create initial balance
    cursor.execute("""
        INSERT INTO accounts (user_id, balance, created_at)
        VALUES (%s, %s, NOW())
        RETURNING id
    """, (user_id, initial_balance))
    
    account_id = cursor.fetchone()[0]
    
    return {
        'user_id': user_id,
        'account_id': account_id,
        'username': username,
        'email': email,
        'initial_balance': initial_balance
    }

# Client request with idempotency
db_idempotency = DatabaseIdempotencyManager(db_connection)

result = db_idempotency.execute_idempotent_operation(
    idempotency_key="user_creation_client_123_req_456",
    operation_type="create_user",
    operation_func=create_user_account,
    request_data={
        'username': 'john_doe',
        'email': 'john@example.com',
        'initial_balance': 100.0
    },
    username='john_doe',
    email='john@example.com',
    initial_balance=100.0
)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>HTTP API Idempotency</h2>
          
          <h3 style={styles.heading}>RESTful API Design</h3>
          <p style={styles.paragraph}>
            Design RESTful APIs with idempotency in mind by choosing appropriate HTTP methods and implementing 
            idempotency keys for non-idempotent operations.
          </p>
          <pre style={styles.codeBlock}>
{`RESTful API Idempotency Design:

# Flask API with idempotency support
from flask import Flask, request, jsonify
import uuid
import time

app = Flask(__name__)

class APIIdempotencyManager:
    def __init__(self, storage):
        self.storage = storage
    
    def get_idempotency_key(self, request):
        """Extract idempotency key from request"""
        return request.headers.get('Idempotency-Key') or request.headers.get('X-Idempotency-Key')
    
    def is_idempotent_method(self, method):
        """Check if HTTP method is naturally idempotent"""
        return method.upper() in ['GET', 'PUT', 'DELETE', 'HEAD', 'OPTIONS']
    
    def require_idempotency_key(self, method):
        """Check if method requires idempotency key"""
        return method.upper() in ['POST', 'PATCH']

idempotency_manager = APIIdempotencyManager(redis_client)

@app.before_request
def check_idempotency():
    """Check idempotency before processing request"""
    method = request.method
    
    if idempotency_manager.require_idempotency_key(method):
        idempotency_key = idempotency_manager.get_idempotency_key(request)
        
        if not idempotency_key:
            return jsonify({
                'error': 'Idempotency-Key header required for POST/PATCH requests',
                'code': 'MISSING_IDEMPOTENCY_KEY'
            }), 400
        
        # Validate idempotency key format
        if len(idempotency_key) < 10 or len(idempotency_key) > 255:
            return jsonify({
                'error': 'Idempotency key must be between 10 and 255 characters',
                'code': 'INVALID_IDEMPOTENCY_KEY'
            }), 400

@app.route('/api/payments', methods=['POST'])
def create_payment():
    """Create payment with idempotency"""
    idempotency_key = idempotency_manager.get_idempotency_key(request)
    request_data = request.get_json()
    
    def process_payment():
        # Validate request
        if not request_data or 'amount' not in request_data:
            raise ValueError("Amount is required")
        
        amount = request_data['amount']
        currency = request_data.get('currency', 'USD')
        user_id = request_data.get('user_id')
        
        if amount <= 0:
            raise ValueError("Amount must be positive")
        
        # Process payment (this would call external payment service)
        payment_id = str(uuid.uuid4())
        
        return {
            'payment_id': payment_id,
            'amount': amount,
            'currency': currency,
            'user_id': user_id,
            'status': 'completed',
            'created_at': time.time()
        }
    
    try:
        result = idempotency_manager.execute_idempotent_operation(
            idempotency_key=f"payment_{idempotency_key}",
            operation_func=process_payment
        )
        
        if result['cached']:
            # Return cached result with 200 (not 201)
            return jsonify(result['result']), 200
        else:
            # Return new result with 201
            return jsonify(result['result']), 201
            
    except ValueError as e:
        return jsonify({
            'error': str(e),
            'code': 'VALIDATION_ERROR'
        }), 400
    except Exception as e:
        return jsonify({
            'error': 'Internal server error',
            'code': 'INTERNAL_ERROR'
        }), 500

@app.route('/api/users/<int:user_id>', methods=['PUT'])
def update_user(user_id):
    """Update user - naturally idempotent"""
    request_data = request.get_json()
    
    # PUT is naturally idempotent - sets resource to specific state
    user_data = {
        'id': user_id,
        'name': request_data.get('name'),
        'email': request_data.get('email'),
        'updated_at': time.time()
    }
    
    # Update user in database
    # This operation is idempotent - same final state regardless of repetition
    update_user_in_db(user_id, user_data)
    
    return jsonify(user_data), 200

@app.route('/api/users/<int:user_id>', methods=['DELETE'])
def delete_user(user_id):
    """Delete user - naturally idempotent"""
    
    # Check if user exists
    if not user_exists(user_id):
        # User already deleted - idempotent response
        return '', 204
    
    # Delete user
    delete_user_from_db(user_id)
    
    return '', 204

@app.route('/api/users', methods=['GET'])
def get_users():
    """Get users - naturally idempotent"""
    users = get_users_from_db()
    return jsonify(users), 200

# Client usage examples
import requests

# POST with idempotency key
response = requests.post(
    'http://api.example.com/payments',
    headers={
        'Idempotency-Key': 'payment_123_abc_def',
        'Content-Type': 'application/json'
    },
    json={
        'amount': 100.0,
        'currency': 'USD',
        'user_id': 123
    }
)

# Retry the same request - will return cached result
response_retry = requests.post(
    'http://api.example.com/payments',
    headers={
        'Idempotency-Key': 'payment_123_abc_def',  # Same key
        'Content-Type': 'application/json'
    },
    json={
        'amount': 100.0,
        'currency': 'USD',
        'user_id': 123
    }
)

# PUT request - naturally idempotent
response = requests.put(
    'http://api.example.com/users/123',
    json={
        'name': 'John Doe',
        'email': 'john@example.com'
    }
)

# DELETE request - naturally idempotent
response = requests.delete('http://api.example.com/users/123')`}
          </pre>

          <h3 style={styles.heading}>Idempotency Key Generation</h3>
          <p style={styles.paragraph}>
            Clients need to generate appropriate idempotency keys that are unique per operation but consistent 
            across retries of the same logical operation.
          </p>
          <pre style={styles.codeBlock}>
{`Idempotency Key Generation Strategies:

1. UUID-based Keys:
   import uuid
   
   # For completely new operations
   idempotency_key = str(uuid.uuid4())
   # Example: "f47ac10b-58cc-4372-a567-0e02b2c3d479"

2. Content-based Keys:
   import hashlib
   
   def generate_content_key(operation_type, user_id, request_data):
       content = f"{operation_type}_{user_id}_{json.dumps(request_data, sort_keys=True)}"
       return hashlib.sha256(content.encode()).hexdigest()[:32]
   
   # Example: "payment_123_a1b2c3d4e5f6"

3. Timestamp-based Keys:
   import time
   
   def generate_timestamp_key(operation_type, user_id):
       timestamp = int(time.time() * 1000)  # milliseconds
       return f"{operation_type}_{user_id}_{timestamp}"
   
   # Example: "payment_123_1634567890123"

4. Client-Generated Keys:
   # Client maintains counter or uses request ID
   client_id = "client_app_v1"
   request_counter = 12345
   idempotency_key = f"{client_id}_req_{request_counter}"
   
   # Example: "client_app_v1_req_12345"

5. Composite Keys:
   def generate_composite_key(user_id, operation_type, business_id, client_request_id):
       return f"{operation_type}_{user_id}_{business_id}_{client_request_id}"
   
   # Example: "payment_123_order_456_client_req_789"

# Client-side retry logic with idempotency
class IdempotentAPIClient:
    def __init__(self, base_url, max_retries=3):
        self.base_url = base_url
        self.max_retries = max_retries
        self.session = requests.Session()
    
    def generate_idempotency_key(self, operation_type, **params):
        """Generate consistent idempotency key"""
        key_data = f"{operation_type}_" + "_".join(f"{k}_{v}" for k, v in sorted(params.items()))
        return hashlib.sha256(key_data.encode()).hexdigest()[:32]
    
    def post_with_idempotency(self, endpoint, data, **key_params):
        """POST request with automatic idempotency key generation and retry"""
        idempotency_key = self.generate_idempotency_key(
            operation_type=endpoint.strip('/').replace('/', '_'),
            **key_params
        )
        
        headers = {
            'Idempotency-Key': idempotency_key,
            'Content-Type': 'application/json'
        }
        
        for attempt in range(self.max_retries + 1):
            try:
                response = self.session.post(
                    f"{self.base_url}{endpoint}",
                    json=data,
                    headers=headers,
                    timeout=30
                )
                
                if response.status_code in [200, 201]:
                    return response.json()
                elif response.status_code == 400:
                    # Client error - don't retry
                    response.raise_for_status()
                elif response.status_code >= 500:
                    # Server error - retry
                    if attempt == self.max_retries:
                        response.raise_for_status()
                    time.sleep(2 ** attempt)  # Exponential backoff
                else:
                    response.raise_for_status()
                    
            except requests.exceptions.RequestException as e:
                if attempt == self.max_retries:
                    raise e
                time.sleep(2 ** attempt)

# Usage
client = IdempotentAPIClient('https://api.example.com')

# Create payment with automatic idempotency
payment_result = client.post_with_idempotency(
    '/api/payments',
    data={
        'amount': 100.0,
        'currency': 'USD'
    },
    user_id=123,
    order_id=456
)

print(payment_result)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Idempotency Patterns</h2>
          
          <h3 style={styles.heading}>Distributed Idempotency</h3>
          <p style={styles.paragraph}>
            In distributed systems with multiple services, idempotency needs to be coordinated across service 
            boundaries to ensure end-to-end operation safety.
          </p>
          <pre style={styles.codeBlock}>
{`Distributed Idempotency Patterns:

1. Saga Pattern with Idempotency:
   
class SagaStep:
    def __init__(self, service_name, operation, compensation):
        self.service_name = service_name
        self.operation = operation
        self.compensation = compensation
        self.idempotency_key = None
        self.status = 'pending'
        self.result = None

class DistributedSaga:
    def __init__(self, saga_id, steps):
        self.saga_id = saga_id
        self.steps = steps
        self.current_step = 0
        self.status = 'running'
    
    def execute(self):
        """Execute saga with idempotent steps"""
        try:
            for i, step in enumerate(self.steps):
                self.current_step = i
                
                # Generate idempotency key for this step
                step.idempotency_key = f"saga_{self.saga_id}_step_{i}"
                
                # Execute step with idempotency
                step.result = self.execute_step_idempotent(step)
                step.status = 'completed'
            
            self.status = 'completed'
            return True
            
        except Exception as e:
            # Compensate completed steps in reverse order
            self.compensate()
            self.status = 'failed'
            raise e
    
    def execute_step_idempotent(self, step):
        """Execute individual step with idempotency"""
        # Check if step already completed
        existing_result = self.get_step_result(step.idempotency_key)
        if existing_result:
            return existing_result
        
        # Execute step
        result = step.operation()
        
        # Store result
        self.store_step_result(step.idempotency_key, result)
        return result
    
    def compensate(self):
        """Compensate completed steps"""
        for i in range(self.current_step, -1, -1):
            step = self.steps[i]
            if step.status == 'completed':
                try:
                    step.compensation()
                except Exception as e:
                    # Log compensation failure
                    print(f"Compensation failed for step {i}: {e}")

# Example: Order processing saga
def create_order_saga(user_id, items, payment_info):
    """Create order with multiple services"""
    
    def reserve_inventory():
        return inventory_service.reserve_items(items, idempotency_key=f"reserve_{saga_id}")
    
    def process_payment():
        return payment_service.charge(payment_info, idempotency_key=f"payment_{saga_id}")
    
    def create_order():
        return order_service.create(user_id, items, idempotency_key=f"order_{saga_id}")
    
    def send_confirmation():
        return notification_service.send_email(user_id, idempotency_key=f"email_{saga_id}")
    
    # Compensation functions
    def release_inventory():
        return inventory_service.release_items(items)
    
    def refund_payment():
        return payment_service.refund(payment_info)
    
    def cancel_order():
        return order_service.cancel(order_id)
    
    def no_compensation():
        pass  # Email doesn't need compensation
    
    saga_id = str(uuid.uuid4())
    
    steps = [
        SagaStep('inventory', reserve_inventory, release_inventory),
        SagaStep('payment', process_payment, refund_payment),
        SagaStep('order', create_order, cancel_order),
        SagaStep('notification', send_confirmation, no_compensation)
    ]
    
    saga = DistributedSaga(saga_id, steps)
    return saga.execute()

2. Event Sourcing with Idempotency:

class Event:
    def __init__(self, event_id, event_type, data, idempotency_key=None):
        self.event_id = event_id
        self.event_type = event_type
        self.data = data
        self.idempotency_key = idempotency_key
        self.timestamp = time.time()

class EventStore:
    def __init__(self):
        self.events = []
        self.idempotency_keys = set()
    
    def append_event(self, event):
        """Append event with idempotency check"""
        if event.idempotency_key and event.idempotency_key in self.idempotency_keys:
            # Event already processed
            return False
        
        self.events.append(event)
        if event.idempotency_key:
            self.idempotency_keys.add(event.idempotency_key)
        
        return True

class OrderAggregate:
    def __init__(self, order_id):
        self.order_id = order_id
        self.events = []
        self.status = 'pending'
        self.items = []
        self.total = 0
    
    def handle_command_idempotent(self, command, idempotency_key):
        """Handle command with idempotency"""
        # Check if command already processed
        if self.command_already_processed(idempotency_key):
            return self.get_command_result(idempotency_key)
        
        # Process command and generate events
        events = self.handle_command(command)
        
        # Store events with idempotency key
        for event in events:
            event.idempotency_key = idempotency_key
            self.apply_event(event)
        
        # Store command result
        result = self.get_current_state()
        self.store_command_result(idempotency_key, result)
        
        return result

3. Two-Phase Commit with Idempotency:

class TwoPhaseCommitCoordinator:
    def __init__(self, participants):
        self.participants = participants
        self.transaction_id = str(uuid.uuid4())
    
    def execute_transaction(self, operations, global_idempotency_key):
        """Execute distributed transaction with idempotency"""
        
        # Check if transaction already completed
        if self.transaction_completed(global_idempotency_key):
            return self.get_transaction_result(global_idempotency_key)
        
        # Phase 1: Prepare
        prepare_results = []
        for i, (participant, operation) in enumerate(zip(self.participants, operations)):
            step_key = f"{global_idempotency_key}_prepare_{i}"
            
            try:
                result = participant.prepare(operation, step_key)
                prepare_results.append(result)
            except Exception as e:
                # Abort transaction
                self.abort_transaction(prepare_results)
                raise e
        
        # Phase 2: Commit
        commit_results = []
        for i, participant in enumerate(self.participants):
            step_key = f"{global_idempotency_key}_commit_{i}"
            
            try:
                result = participant.commit(step_key)
                commit_results.append(result)
            except Exception as e:
                # This is problematic - some participants may have committed
                # Need to implement recovery mechanism
                self.handle_commit_failure(i, commit_results)
                raise e
        
        # Store transaction result
        final_result = self.aggregate_results(commit_results)
        self.store_transaction_result(global_idempotency_key, final_result)
        
        return final_result`}
          </pre>

          <h3 style={styles.heading}>Idempotency in Message Queues</h3>
          <p style={styles.paragraph}>
            Message processing systems need idempotency to handle duplicate messages, redeliveries, and 
            processing failures safely.
          </p>
          <pre style={styles.codeBlock}>
{`Message Queue Idempotency:

class IdempotentMessageProcessor:
    def __init__(self, storage_backend):
        self.storage = storage_backend
        self.processed_messages = set()
    
    def process_message_idempotent(self, message):
        """Process message with idempotency guarantee"""
        message_id = message.get('message_id') or message.get('id')
        
        if not message_id:
            raise ValueError("Message must have an ID for idempotent processing")
        
        # Check if message already processed
        if self.is_message_processed(message_id):
            print(f"Message {message_id} already processed, skipping")
            return self.get_processing_result(message_id)
        
        # Mark message as being processed
        self.mark_message_processing(message_id)
        
        try:
            # Process the message
            result = self.process_message(message)
            
            # Mark as successfully processed
            self.mark_message_processed(message_id, result)
            
            return result
            
        except Exception as e:
            # Mark as failed (allow retry)
            self.mark_message_failed(message_id, str(e))
            raise e
    
    def is_message_processed(self, message_id):
        """Check if message was already processed"""
        status = self.storage.get(f"msg_status:{message_id}")
        return status == 'processed'
    
    def mark_message_processing(self, message_id):
        """Mark message as currently being processed"""
        self.storage.setex(f"msg_status:{message_id}", 300, 'processing')
    
    def mark_message_processed(self, message_id, result):
        """Mark message as successfully processed"""
        self.storage.setex(f"msg_status:{message_id}", 86400, 'processed')
        self.storage.setex(f"msg_result:{message_id}", 86400, json.dumps(result))
    
    def get_processing_result(self, message_id):
        """Get cached processing result"""
        result_json = self.storage.get(f"msg_result:{message_id}")
        return json.loads(result_json) if result_json else None

# Kafka consumer with idempotency
from kafka import KafkaConsumer
import json

class IdempotentKafkaConsumer:
    def __init__(self, topics, group_id, storage_backend):
        self.consumer = KafkaConsumer(
            *topics,
            group_id=group_id,
            value_deserializer=lambda x: json.loads(x.decode('utf-8')),
            enable_auto_commit=False  # Manual commit after processing
        )
        self.processor = IdempotentMessageProcessor(storage_backend)
    
    def consume_messages(self):
        """Consume messages with idempotent processing"""
        for message in self.consumer:
            try:
                # Extract message data
                message_data = message.value
                
                # Generate idempotency key from message
                idempotency_key = self.generate_message_key(message)
                message_data['message_id'] = idempotency_key
                
                # Process message idempotently
                result = self.processor.process_message_idempotent(message_data)
                
                # Commit offset only after successful processing
                self.consumer.commit()
                
                print(f"Processed message {idempotency_key}: {result}")
                
            except Exception as e:
                print(f"Failed to process message: {e}")
                # Don't commit offset - message will be redelivered
    
    def generate_message_key(self, message):
        """Generate idempotency key from message metadata"""
        # Use partition + offset as unique identifier
        return f"kafka_{message.topic}_{message.partition}_{message.offset}"

# RabbitMQ consumer with idempotency
import pika

class IdempotentRabbitMQConsumer:
    def __init__(self, queue_name, storage_backend):
        self.connection = pika.BlockingConnection(pika.ConnectionParameters('localhost'))
        self.channel = self.connection.channel()
        self.queue_name = queue_name
        self.processor = IdempotentMessageProcessor(storage_backend)
    
    def callback(self, ch, method, properties, body):
        """Message callback with idempotent processing"""
        try:
            message_data = json.loads(body)
            
            # Use message ID from properties or generate one
            message_id = properties.message_id or f"rabbitmq_{method.delivery_tag}"
            message_data['message_id'] = message_id
            
            # Process idempotently
            result = self.processor.process_message_idempotent(message_data)
            
            # Acknowledge message only after successful processing
            ch.basic_ack(delivery_tag=method.delivery_tag)
            
            print(f"Processed message {message_id}: {result}")
            
        except Exception as e:
            print(f"Failed to process message: {e}")
            # Reject message - it will be requeued
            ch.basic_nack(delivery_tag=method.delivery_tag, requeue=True)
    
    def start_consuming(self):
        """Start consuming messages"""
        self.channel.queue_declare(queue=self.queue_name, durable=True)
        self.channel.basic_consume(
            queue=self.queue_name,
            on_message_callback=self.callback,
            auto_ack=False  # Manual acknowledgment
        )
        
        print(f"Starting to consume from {self.queue_name}")
        self.channel.start_consuming()

# Usage
redis_client = redis.Redis(host='localhost', port=6379, db=0)

# Kafka consumer
kafka_consumer = IdempotentKafkaConsumer(
    topics=['order_events', 'payment_events'],
    group_id='order_processor',
    storage_backend=redis_client
)

# RabbitMQ consumer  
rabbitmq_consumer = IdempotentRabbitMQConsumer(
    queue_name='order_processing_queue',
    storage_backend=redis_client
)

# Start consuming
kafka_consumer.consume_messages()
# rabbitmq_consumer.start_consuming()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Stripe's Idempotency</h3>
          <p style={styles.paragraph}>
            Stripe implements comprehensive idempotency for all API operations using idempotency keys. They store 
            the complete request and response for 24 hours, ensuring that retried requests return identical results.
          </p>

          <h3 style={styles.heading}>AWS API Idempotency</h3>
          <p style={styles.paragraph}>
            AWS services like EC2 and S3 implement idempotency differently - EC2 uses client tokens for operations 
            like instance creation, while S3 uses ETags and conditional requests for object operations.
          </p>

          <h3 style={styles.heading}>PayPal's Payment Processing</h3>
          <p style={styles.paragraph}>
            PayPal uses idempotency keys to prevent duplicate payments and implements sophisticated deduplication 
            logic that considers both request content and timing to handle edge cases safely.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Use appropriate idempotency strategies:</strong> Choose between natural idempotency, 
              idempotency keys, or content-based deduplication based on your use case.
            </li>
            <li style={styles.listItem}>
              <strong>Set reasonable TTLs:</strong> Store idempotency records long enough to handle retries 
              but not so long as to consume excessive storage.
            </li>
            <li style={styles.listItem}>
              <strong>Handle partial failures gracefully:</strong> Design operations to be safely retryable 
              even when they partially succeed.
            </li>
            <li style={styles.listItem}>
              <strong>Validate idempotency keys:</strong> Ensure keys are unique, well-formatted, and 
              appropriate for the operation being performed.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor idempotency effectiveness:</strong> Track duplicate requests, cache hit rates, 
              and retry patterns to optimize your implementation.
            </li>
            <li style={styles.listItem}>
              <strong>Document idempotency behavior:</strong> Clearly communicate to API consumers which 
              operations are idempotent and how to use idempotency keys properly.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Idempotent operations are fundamental to building reliable distributed systems. They enable safe retries, 
            prevent duplicate side effects, and provide consistency guarantees in the face of network failures and 
            system outages.
          </p>
          <p style={styles.paragraph}>
            Implement idempotency early in your system design, choose appropriate strategies for different types of 
            operations, and always test your idempotency implementation thoroughly with various failure scenarios 
            to ensure it works correctly under all conditions.
          </p>
        </section>
      </main>
    </article>
  );
};

export default IdempotentOperations;
